/**
 * @author       calvin@zumper.com (Calvin Yau)
 * @copyright    Copyright (c) 2020, Zumper
 */

import { Hours, type SerializableDate, type ValidHour } from '@zcode/bizos'
import { getDay } from '@zcode/nr/es/shared/utils/date'
import { oisEmpty } from '@zcode/nr/es/shared/utils/utils'
import {
  addDays,
  addMinutes,
  getHours,
  getMinutes,
  isBefore,
  isValid,
  set,
  setHours,
  setMinutes,
  toDate,
} from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

export const EARLIEST_WEEKDAY_HOUR = 9
export const EARLIEST_WEEKEND_HOUR = 10
export const LATEST_WEEKEND_HOUR = 16
export const LATEST_WEEKDAY_HOUR = 17
export const STANDARD_INTERVAL_MIN = 15
export const MAX_DATE_CHOICES_COUNT = 14
export const MAX_DATE_CHOICES_COUNT_KNOCK = 15
export const DEFAULT_TZ = 'America/Chicago'
// max tiles in tour picker
export const INTERVAL_MAX_LENGTH = 12

export function eachDayOfIntervalInTz(
  { end, start }: { end: Date | number; start: Date | number },
  tz: string | null | undefined
): Date[] {
  const startDate = toDate(start)
  const endDate = toDate(end)
  const endTime = endDate.getTime()
  if (!(startDate.getTime() <= endTime)) {
    throw new RangeError('Invalid interval')
  }
  const dates = []
  const currentDate = startOfDayInTz(startDate, tz)
  while (currentDate.getTime() <= endTime) {
    dates.push(new Date(currentDate.getTime()))
    currentDate.setDate(currentDate.getDate() + 1)
    if (dates.length >= INTERVAL_MAX_LENGTH) {
      break
    }
  }
  return dates
}

export function getDayInTz(date: Date, tz: string | null | undefined): 0 | 1 | 2 | 3 | 4 | 5 | 6 {
  // @ts-expect-error number isn't assignable to 0|1|2|3|4|5|6
  return Number.parseInt(formatInTimeZone(date, tz ?? DEFAULT_TZ, 'e'), 10) - 1
}

function setHoursInTz(date: Date, hour: number, tz: string | null | undefined): Date {
  return new Date(
    formatInTimeZone(date.setHours(hour), tz ?? DEFAULT_TZ, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX")
  )
}

function startOfDayInTz(date: Date, tz: string | null | undefined): Date {
  return new Date(formatInTimeZone(date, tz ?? DEFAULT_TZ, "yyyy-MM-dd'T00:00:00.000'XXX"))
}

export function toDateStringInTz(
  date: Date | SerializableDate,
  tz: string | null | undefined
): ReturnType<Date['toDateString']> {
  if (!isValid(date)) {
    return 'Invalid Date'
  }
  // @ts-expect-error: string is not assignable to `${'Sun' | 'Mon' | ...} ${string} ${number} ${string}`
  return formatInTimeZone(date, tz ?? DEFAULT_TZ, 'EEE MMM dd yyyy')
}

/**
 * @param dateObj   date object of reference time
 * @returns date that is two days later with the starting hour
 */
export const getTwoDaysAhead = (dateObj: Date, tz: string | null | undefined) => {
  if (!isValid(dateObj)) {
    return null
  }

  const twoDaysForward = startOfDayInTz(addDays(dateObj, 2), tz)
  const dayOfWeek = getDayInTz(twoDaysForward, tz)

  const earliestHour =
    dayOfWeek === 0 || dayOfWeek === 6 ? EARLIEST_WEEKEND_HOUR : EARLIEST_WEEKDAY_HOUR
  const twoDaysForwardEarliestTime = setHoursInTz(twoDaysForward, earliestHour, tz)

  return twoDaysForwardEarliestTime
}

export const getOneDayAhead = (dateObj: Date, tz: string | null | undefined) => {
  if (!isValid(dateObj)) {
    return null
  }

  const oneDayForward = startOfDayInTz(addDays(dateObj, 1), tz)
  const dayOfWeek = getDayInTz(oneDayForward, tz)

  const earliestHour =
    dayOfWeek === 0 || dayOfWeek === 6 ? EARLIEST_WEEKEND_HOUR : EARLIEST_WEEKDAY_HOUR
  const oneDayForwardEarliestTime = setHoursInTz(oneDayForward, earliestHour, tz)

  return oneDayForwardEarliestTime
}

/**
 * gives the default days for availabilities
 *
 * @param dateObj   date object of reference time
 * @returns array of days in date object format
 */
export const getDefaultDateChoices = (dateObj: Date, tz: string | null | undefined) => {
  const choices: Date[] = []
  if (!isValid(dateObj)) {
    return choices
  }
  const twoDaysAhead = getTwoDaysAhead(dateObj, tz)
  if (!twoDaysAhead) {
    return choices
  }
  for (let i = 0; i < MAX_DATE_CHOICES_COUNT; ++i) {
    const nextDay = addDays(twoDaysAhead, i)
    const nextDayNum = getDayInTz(nextDay, tz)
    if (nextDayNum === 0 || nextDayNum === 6) {
      choices.push(setHoursInTz(addDays(twoDaysAhead, i), EARLIEST_WEEKEND_HOUR, tz))
    } else {
      choices.push(setHoursInTz(addDays(twoDaysAhead, i), EARLIEST_WEEKDAY_HOUR, tz))
    }
  }
  return choices
}

/**
 * gives interval choices for a date separated by day
 *
 * @param interval.interval
 * @param interval.interval
 * @param interval    duration of a tour slot in minutes
 * @param dateObj     date object of reference time that we want choices for
 * @param interval.dateObj
 * @param interval.dateObj
 * @returns map with keys of days and values of arrays of interval times for that day
 */
export const getDefaultIntervalChoices = ({
  interval = STANDARD_INTERVAL_MIN,
  dateObj,
  tz,
}: { interval?: number; dateObj: Date; tz: string | null | undefined }) => {
  const dayChoices = getDefaultDateChoices(dateObj, tz)
  return dayChoices.reduce((result, day) => {
    let currentChoice = day
    const dayOfWeek = getDayInTz(day, tz)
    const dailyIntervals = []
    const latestHour =
      dayOfWeek === 0 || dayOfWeek === 6 ? LATEST_WEEKEND_HOUR : LATEST_WEEKDAY_HOUR
    while (getHours(currentChoice) < latestHour) {
      dailyIntervals.push(currentChoice)
      currentChoice = addMinutes(currentChoice, interval)
    }
    result.set(toDateStringInTz(day, tz), dailyIntervals)
    return result
  }, new Map<ReturnType<Date['toDateString']>, readonly Date[]>())
}

export const getNextPossibleTimeOfficeHours = (
  hours: readonly Hours[],
  now: number,
  tz: string | null | undefined
) => {
  if (!hours?.length) {
    return
  }
  const intervalChoicesObjByDate = getIntervalChoicesFromOfficeHours({
    currentDateObj: new Date(now),
    hours,
    tz,
  })
  const firstDate = intervalChoicesObjByDate && getFirstKey(intervalChoicesObjByDate)
  if (intervalChoicesObjByDate && firstDate && intervalChoicesObjByDate.get(firstDate)?.length) {
    // select first time of first day
    const firstDateIntervals = intervalChoicesObjByDate.get(firstDate)
    return firstDateIntervals?.[0]
  }
}

function getFirstKey<K>(map: ReadonlyMap<K, unknown>) {
  const first = map.keys().next()
  if (!first.done) {
    return first.value
  }
}

export const getKnockIntervalChoices = (
  dayChoices: readonly Date[],
  now: number,
  tz: string | null
) => {
  const result = new Map<ReturnType<Date['toDateString']>, Date[]>()
  const today = new Date(now)
  const dayAfterLatestDay = startOfDayInTz(addDays(today, MAX_DATE_CHOICES_COUNT_KNOCK), tz)

  if (Array.isArray(dayChoices) && dayChoices.length) {
    for (const day of dayChoices) {
      const currentChoice = day
      const dateKey = toDateStringInTz(day, tz)
      const timesForThisDate = result.get(dateKey)
      if (timesForThisDate && isBefore(currentChoice, dayAfterLatestDay)) {
        timesForThisDate.push(currentChoice)
      } else if (!timesForThisDate && isBefore(currentChoice, dayAfterLatestDay)) {
        result.set(dateKey, [currentChoice])
      }
    }
  }
  return result
}

/**
 * Copies hours and minutes of oldDate into newDate
 *
 * @example copyHoursMinutes('2023-01-02 03:45', '2026-07-08 09:10') => '2026-07-08 03:45'
 */
export function copyHoursMinutes(oldDate: Date | SerializableDate, newDate: Date) {
  return setMinutes(setHours(newDate, getHours(oldDate.getTime())), getMinutes(oldDate.getTime()))
}

/**
 * convert office hours into a schedule for looking up times for each day of the week
 *
 * @param hours
 */
export function getOfficeHoursSchedule(hours: readonly Hours[]) {
  if (Array.isArray(hours) && hours.length) {
    const validHoursForSchedule = hours.filter(
      (
        hour
      ): hour is Hours & {
        day: NonNullable<Hours['day']>
        end: ValidHour
        start: ValidHour
        startTime: NonNullable<Hours['startTime']>
        endTime: NonNullable<Hours['endTime']>
      } =>
        !!hour.day &&
        !!(hour.start && hour.end) &&
        Hours.isValidHour(hour.start) &&
        Hours.isValidHour(hour.end) &&
        !!hour.startTime &&
        !!hour.endTime
    )
    if (Array.isArray(validHoursForSchedule) && validHoursForSchedule.length) {
      const schedule: {
        [day in Day]?: NonNullable<ReturnType<typeof getDay>> & {
          endTime: NonNullable<Hours['endTime']>
          startTime: NonNullable<Hours['startTime']>
        }
      } = {}
      for (const hourEntry of validHoursForSchedule) {
        const day = getDay(hourEntry.day, 'key')
        const { startTime, endTime } = hourEntry || {}

        if (!startTime || !endTime || !day) {
          return null
        }

        schedule[day.value] = { ...day, startTime, endTime }
      }
      return schedule
    }
  }
  return null
}

export const getIntervalChoicesFromOfficeHours = ({
  hours,
  currentDateObj,
  interval = STANDARD_INTERVAL_MIN,
  tz,
}: {
  hours: readonly Hours[]
  currentDateObj: Date
  interval?: number
  tz: string | null | undefined
}) => {
  const schedule = getOfficeHoursSchedule(hours)

  if (schedule) {
    const choices = new Map<ReturnType<Date['toDateString']>, Date[]>()

    const startDay = getTwoDaysAhead(currentDateObj, tz)

    if (!startDay) {
      return choices
    }

    for (let i = 0; i < MAX_DATE_CHOICES_COUNT; ++i) {
      const dayIndex = addDays(startDay, i)
      const scheduleDayEntry = schedule[getDayInTz(dayIndex, tz)]
      const dailyIntervalChoices = []

      if (
        scheduleDayEntry &&
        !oisEmpty(scheduleDayEntry.startTime) &&
        !oisEmpty(scheduleDayEntry.endTime)
      ) {
        const { startTime, endTime } = scheduleDayEntry
        const endTimeDateObj = set(dayIndex, { hours: endTime?.hour, minutes: endTime?.minute })
        let currentChoice = set(dayIndex, { hours: startTime?.hour, minutes: startTime?.minute })
        while (isBefore(currentChoice, endTimeDateObj)) {
          dailyIntervalChoices.push(currentChoice)
          currentChoice = addMinutes(currentChoice, interval)
        }
      }
      if (dailyIntervalChoices.length) {
        choices.set(toDateStringInTz(dayIndex, tz), dailyIntervalChoices)
      }
    }

    return choices
  }

  return null
}
